import React, { useEffect, useState } from "react";
import { useTranslate } from "react-polyglot";
import { useHistory, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core";
import clsx from "clsx";
import useEffectWithPreviousValues from "../../../../hooks/useEffectWithPreviousValues.js";
import FlexboxVertical from "../../../../SharedComponents/FlexboxVertical.jsx";
import ExamProgressTypography from "../../../../SharedComponents/ExamProgressTypography.jsx";
import TimerChip from "../../../../SharedComponents/TimerChip.jsx";
import { formatTime } from "../../../../Utils/misc.js";
import ExamResultPopup from "./SharedComponents/ExamResultPopup.jsx";
import { useLearnProcess } from "../../../../Providers/Data/LearnProcessProvider.jsx";
import ExamStatusText from "../../../../SharedComponents/ExamStatusText.jsx";
import icCheck from "../../../../assets/ic_check_green.svg";
import icPlus from "../../../../assets/ic_plus_pink.svg";
import { useAuth } from "../../../../Providers/Auth/AuthProvider.jsx";

const useStyles = makeStyles(
  () => ({
    learnedStatusIcon: {
      width: 20,
      height: 20,
      marginBottom: -3,
      marginLeft: 8,
    },
    wrongAnswerIcon: {
      transform: "rotate(45deg)",
    },
  }),
  {
    name: "ExamInfoBlock",
  },
);

/**
 * @param {ExerciseResponse} exercise
 */
const ExamInfoBlock = ({ exercise }) => {
  const classes = useStyles();
  const t = useTranslate();
  const { requestOtherExercise, restartExam } = useLearnProcess();
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();
  const { isViewExamDeck } = location.state || {};
  const [examResultOpen, setExamResultOpen] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const exam = exercise.deck.exam;

  // Start exam's timer. Если timeLimit < 0, экзамен бессрочный — таймер не запускаем
  useEffect(() => {
    if (!exam.startDate || isViewExamDeck || exam.timeLimit < 0) {
      return;
    }
    const end = exam.startDate + exam.timeLimit * 60 * 1000;
    let now = Date.now();
    if (end - now <= 0 || (exam.status !== "in_progress" && exam.status !== "none")) {
      setElapsedTime(0);
      return;
    }
    setElapsedTime(end - now);
    const timerHandle = setInterval(() => {
      now = Date.now();
      if (end - now <= 0) {
        clearInterval(timerHandle);
        timeIsOut();
        return;
      }
      setElapsedTime(end - now);
    }, 1000);

    return () => {
      clearInterval(timerHandle);
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exercise]);

  // observe exam status
  useEffectWithPreviousValues(
    /** @param {ExerciseResponse} prevExercise */
    ([prevExercise]) => {
      if (
        prevExercise &&
        prevExercise.deck.id === exercise.deck.id &&
        prevExercise.deck.exam.status !== exam.status &&
        exam.status !== "in_progress"
      ) {
        setExamResultOpen(true);
      }
    },
    [exercise],
  );

  /** */
  const timeIsOut = () => {
    setTimeout(() => {
      requestOtherExercise(0).then();
    }, 1000);
  };

  /**
   * @param {"retake" | "view" | "close"} mode
   */
  const examResultPopupPositiveButtonHandler = (mode) => {
    if (exam.status !== "none" && exam.status !== "in_progress") {
      if (mode === "close") {
        history.push("/");
      } else if (mode === "view") {
        history.push({ pathname: `/learn/${exercise.deck.id}`, state: { isViewExamDeck: true } });
      } else {
        restartExam();
      }
    }
  };

  return (
    <>
      <FlexboxVertical alignItems="center">
        <ExamProgressTypography color="primary">
          {t("exam.progress", { current: exercise.exercise.position + 1, total: exercise.deck.count })}
          {/* Для Persona Education иконки показываем всегда */}
          {(exam.endDate !== null || user?.companyId === 4472) && exercise.exercise.learned && (
            <img className={classes.learnedStatusIcon} src={icCheck} alt="" />
          )}
          {(exam.endDate !== null || user?.companyId === 4472) &&
            !exercise.exercise.learned &&
            exercise.exercise.passed && (
              <img className={clsx(classes.learnedStatusIcon, classes.wrongAnswerIcon)} src={icPlus} alt="" />
            )}
        </ExamProgressTypography>
        {exam.endDate === null && exam.timeLimit >= 0 && <TimerChip label={formatTime(elapsedTime / 1000)} />}
        {exam.endDate !== null && <ExamStatusText exam={exam} />}
      </FlexboxVertical>
      <ExamResultPopup
        exam={exam}
        open={examResultOpen}
        onClose={(mode) => {
          examResultPopupPositiveButtonHandler(mode);
          setExamResultOpen(false);
        }}
      />
    </>
  );
};

ExamInfoBlock.propTypes = {
  exercise: PropTypes.object.isRequired,
};

export default ExamInfoBlock;
